import { useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import type { MarketingCampaign } from '@/lib/types';
import { Button, ConfirmDialog, Field, Modal, Textarea } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

export interface ApprovalBannerProps {
  campaign: MarketingCampaign;
  canApprove: boolean;
  onChanged(): void;
}

function personName(person: { name: string; email: string } | null | undefined): string {
  if (!person) return 'someone';
  return person.name.trim() || person.email;
}

/**
 * The approval state of a campaign: awaiting a decision (with Approve / Reject
 * for a holder of `marketing-campaigns.approve`), or the last rejection on a
 * draft. Renders nothing otherwise.
 */
export function ApprovalBanner({ campaign, canApprove, onChanged }: ApprovalBannerProps) {
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reasonError, setReasonError] = useState<string | null>(null);

  const { approval } = campaign;
  const pending = campaign.status === 'pending_approval';
  const rejectedDraft = campaign.status === 'draft' && approval.rejectedAt !== null;
  if (!pending && !rejectedDraft) return null;

  function describe(caught: unknown, fallback: string): string {
    if (caught instanceof ApiError) return caught.formMessage ?? caught.message;
    return fallback;
  }

  async function approve() {
    setBusy(true);
    setError(null);
    try {
      await api.approveMarketingCampaign(campaign.id);
      setApproveOpen(false);
      onChanged();
    } catch (caught) {
      setError(describe(caught, 'Could not reach the server. The campaign was not approved.'));
    } finally {
      setBusy(false);
    }
  }

  async function reject() {
    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonError('Say why, so the requester knows what to change.');
      return;
    }
    setBusy(true);
    setError(null);
    setReasonError(null);
    try {
      await api.rejectMarketingCampaign(campaign.id, trimmed);
      setRejectOpen(false);
      setReason('');
      onChanged();
    } catch (caught) {
      if (caught instanceof ApiError && caught.fieldErrors.reason) setReasonError(caught.fieldErrors.reason);
      else setError(describe(caught, 'Could not reach the server. The campaign was not rejected.'));
    } finally {
      setBusy(false);
    }
  }

  if (rejectedDraft) {
    return (
      <Notice
        tone="danger"
        role="status"
        title={
          <>
            Rejected by {personName(approval.rejectedBy)}
            {approval.rejectedAt ? ` on ${formatDateTime(approval.rejectedAt)}` : ''}
          </>
        }
      >
        {approval.rejectionReason && (
          <p className="m-0 whitespace-pre-line [overflow-wrap:anywhere]">{approval.rejectionReason}</p>
        )}
      </Notice>
    );
  }

  const estimate = campaign.recipientEstimate;

  return (
    <>
      <Notice
        tone="warning"
        title="Waiting for approval"
        action={
          canApprove ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" onClick={() => setRejectOpen(true)} disabled={busy}>
                Reject
              </Button>
              <Button variant="primary" size="sm" onClick={() => setApproveOpen(true)} disabled={busy}>
                Approve
              </Button>
            </div>
          ) : undefined
        }
      >
        <p className="m-0 [overflow-wrap:anywhere]">
          Requested by {personName(approval.requestedBy)}
          {approval.requestedAt ? ` on ${formatDateTime(approval.requestedAt)}` : ''}
          {estimate !== null ? ` · about ${estimate.toLocaleString()} recipients` : ''}. Nothing sends until it is approved.
        </p>
        {error && (
          <p role="alert" className="mt-1 mb-0 text-danger [overflow-wrap:anywhere]">
            {error}
          </p>
        )}
      </Notice>

      <ConfirmDialog
        open={approveOpen}
        onClose={() => {
          if (!busy) setApproveOpen(false);
        }}
        onConfirm={() => void approve()}
        title={`Approve ${campaign.name}?`}
        description={
          campaign.scheduledAt
            ? `It is scheduled and starts sending at ${formatDateTime(campaign.scheduledAt)}${
                estimate !== null ? ` to about ${estimate.toLocaleString()} recipients` : ''
              }.`
            : `It starts sending as soon as it is approved${
                estimate !== null ? `, to about ${estimate.toLocaleString()} recipients` : ''
              }.`
        }
        confirmLabel="Approve"
        tone="primary"
        loading={busy}
        error={error}
      />

      <Modal
        open={rejectOpen}
        onClose={() => {
          if (!busy) setRejectOpen(false);
        }}
        title={`Reject ${campaign.name}?`}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRejectOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="danger-solid" onClick={() => void reject()} loading={busy}>
              Reject
            </Button>
          </>
        }
      >
        <div className="grid gap-3">
          <p className="m-0 text-sm text-fg-secondary">It goes back to draft. Nothing is sent.</p>
          <Field label="Reason" required error={reasonError}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                rows={3}
                maxLength={1000}
                value={reason}
                disabled={busy}
                invalid={Boolean(reasonError)}
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          {error && (
            <p role="alert" className="m-0 text-xs text-danger [overflow-wrap:anywhere]">
              {error}
            </p>
          )}
        </div>
      </Modal>
    </>
  );
}
